"use client";

import { request } from "@/config/request";
import { useEffect } from "react";

/** 商品本币价格节点的选择器。 */
const PRODUCT_PRICE_SELECTOR = "[data-cny-price]";

/** 等待按当前用户重新获取价格的商品容器选择器。 */
const PERSONALIZED_PRODUCT_PRICE_SELECTOR =
  ".article-product-price[data-product-code]";

/** 商品价格进入视口前的预加载距离。 */
const PRODUCT_PRICE_ROOT_MARGIN = "400px 0px";

/** 等待页头汇率初始化的轮询间隔。 */
const PRICE_UPDATE_INTERVAL_MS = 200;

/** 等待页头汇率初始化的最大轮询次数。 */
const PRICE_UPDATE_MAX_ATTEMPTS = 50;

/** 商品价格增强器参数。 */
interface ArticleProductEnhancerProps {
  articleId?: number;
}

/** 带有全局人民币换算函数的页面 Window。 */
interface ProductPriceWindow extends Window {
  calcByCnyPrice?: (price: string) => string;
}

/** 商品详情中用于选择当前用户价格的字段。 */
interface PersonalizedProductData {
  productOriginPriceRange?: string;
  productSellPrice?: number | string;
  productSellPriceRange?: string;
}

/** 按旧版店铺节点规则隐藏匿名用户可见的换算价格。 */
const maskPublishedPrice = (price: string): string => {
  /** 换算价格的整数部分。 */
  const integerPart = price.split(".")[0] || "";
  if (integerPart.length <= 1) return "*";
  return `${integerPart[0]}${"*".repeat(integerPart.length - 1)}`;
};

/** 使用页头已经加载的站点汇率更新商品本币价格。 */
const updatePublishedProductPrices = (): boolean => {
  /** 页面提供的人民币换算函数。 */
  const calculateConvertedPrice = (window as ProductPriceWindow).calcByCnyPrice;
  if (!calculateConvertedPrice) return false;

  /** 正文内等待换算的商品价格节点。 */
  const priceElements = Array.from(
    document.querySelectorAll<HTMLElement>(PRODUCT_PRICE_SELECTOR),
  );
  if (!priceElements.length) return true;

  /** 已完成换算的商品价格数量。 */
  let resolvedPriceCount = 0;
  priceElements.forEach((element) => {
    /** 当前节点保存的人民币价格。 */
    const cnyPrice = element.dataset.cnyPrice;
    if (!cnyPrice) return;

    /** 当前人民币价格换算后的站点本币金额。 */
    const convertedPrice = calculateConvertedPrice(cnyPrice);
    if (!convertedPrice || convertedPrice === "***") return;
    /** 当前价格是否来自需要登录后展示完整金额的店铺节点。 */
    const isMasked = element.dataset.priceMasked === "true";
    element.textContent = isMasked
      ? maskPublishedPrice(convertedPrice)
      : convertedPrice;
    resolvedPriceCount += 1;
  });
  return resolvedPriceCount === priceElements.length;
};

/** 请求当前登录用户可见的人民币商品价格。 */
const requestPersonalizedProductPrice = async (
  productCode: string,
): Promise<string | undefined> => {
  /** 携带浏览器当前 token、店铺和站点信息的商品详情响应。 */
  const response = await request.goods.product.detail({ productCode });
  /** 当前用户权限下返回的商品详情。 */
  const product = response.data as PersonalizedProductData | undefined;
  /** 按销售价、销售区间、原始区间依次选择的价格候选值。 */
  const priceCandidates = [
    product?.productSellPrice,
    product?.productSellPriceRange,
    product?.productOriginPriceRange,
  ];
  /** 当前用户最终可见的非空价格。 */
  const price = priceCandidates.find(
    (candidate) =>
      candidate !== undefined &&
      candidate !== null &&
      String(candidate).trim() !== "",
  );
  if (price === undefined || price === null) return undefined;

  /** 标准化后的人民币价格文本。 */
  const priceText = String(price).trim();
  return priceText;
};

/** 将当前用户价格写入人民币节点并触发日元等本币换算。 */
const updatePersonalizedPriceElements = (
  elements: HTMLElement[],
  cnyPrice?: string,
) => {
  elements.forEach((element) => {
    /** 当前价格容器中的人民币展示节点。 */
    const cnyElement = element.querySelector<HTMLElement>(
      ".article-product-price-cny",
    );
    /** 当前价格容器中的本币换算节点。 */
    const convertedElement = element.querySelector<HTMLElement>(
      PRODUCT_PRICE_SELECTOR,
    );
    /** 服务端占位内容中保留的人民币单位。 */
    const cnyUnit =
      cnyElement?.textContent?.replace("***", "").trim() || "CNY";
    /** 当前价格是否需要继续按匿名店铺规则脱敏。 */
    const isMasked = element.dataset.priceMasked === "true";

    if (cnyElement) {
      cnyElement.textContent = cnyPrice
        ? `${isMasked ? maskPublishedPrice(cnyPrice) : cnyPrice} ${cnyUnit}`
        : `-- ${cnyUnit}`;
    }
    if (convertedElement) {
      convertedElement.dataset.cnyPrice = cnyPrice || "";
      if (!cnyPrice) convertedElement.textContent = "--";
    }
    element.setAttribute("aria-busy", "false");
  });

  if (cnyPrice) updatePublishedProductPrices();
};

/** 在不加载 Lexical EditorView 的情况下增强发布页商品价格。 */
const ArticleProductEnhancer = ({
  articleId,
}: ArticleProductEnhancerProps) => {
  useEffect(() => {
    /** 当前已执行的汇率更新次数。 */
    let updateAttempts = 0;
    if (updatePublishedProductPrices()) return;

    /** 定时等待页头异步加载站点汇率。 */
    const intervalId = window.setInterval(() => {
      updateAttempts += 1;
      if (
        updatePublishedProductPrices() ||
        updateAttempts >= PRICE_UPDATE_MAX_ATTEMPTS
      ) {
        window.clearInterval(intervalId);
      }
    }, PRICE_UPDATE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [articleId]);

  useEffect(() => {
    /** 当前组件是否已经卸载。 */
    let isDisposed = false;
    /** 正文内等待获取个性价格的商品节点。 */
    const personalizedPriceElements = Array.from(
      document.querySelectorAll<HTMLElement>(
        PERSONALIZED_PRODUCT_PRICE_SELECTOR,
      ),
    );
    if (!personalizedPriceElements.length) return;

    /** 按商品编码归组的全部价格节点。 */
    const priceElementsByCode = new Map<string, HTMLElement[]>();
    personalizedPriceElements.forEach((element) => {
      /** 当前价格节点保存的商品编码。 */
      const productCode = element.dataset.productCode;
      if (!productCode) return;
      /** 同一商品编码已经登记的价格节点。 */
      const groupedElements = priceElementsByCode.get(productCode) || [];
      groupedElements.push(element);
      priceElementsByCode.set(productCode, groupedElements);
    });

    /** 本次页面访问已经发起请求的商品编码。 */
    const requestedProductCodes = new Set<string>();
    /** 按商品编码加载一次价格，并同步更新文章内的重复节点。 */
    const loadPersonalizedProductPrice = async (productCode: string) => {
      if (requestedProductCodes.has(productCode)) return;
      requestedProductCodes.add(productCode);

      try {
        /** 当前登录用户可以看到的人民币商品价格。 */
        const cnyPrice = await requestPersonalizedProductPrice(productCode);
        if (isDisposed) return;
        /** 当前商品编码对应的全部价格节点。 */
        const groupedElements = priceElementsByCode.get(productCode) || [];
        updatePersonalizedPriceElements(groupedElements, cnyPrice);
      } catch (error) {
        if (isDisposed) return;
        console.error("Failed to resolve personalized product price", {
          error,
          productCode,
        });
        /** 当前请求失败后需要结束加载状态的价格节点。 */
        const groupedElements = priceElementsByCode.get(productCode) || [];
        updatePersonalizedPriceElements(groupedElements);
      }
    };

    if (!("IntersectionObserver" in window)) {
      priceElementsByCode.forEach((_elements, productCode) => {
        void loadPersonalizedProductPrice(productCode);
      });
      return () => {
        isDisposed = true;
      };
    }

    /** 在商品卡片接近可视区域时才触发个性价格请求。 */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          /** 进入预加载区域的商品价格节点。 */
          const element = entry.target as HTMLElement;
          /** 当前可见价格节点保存的商品编码。 */
          const productCode = element.dataset.productCode;
          observer.unobserve(element);
          if (productCode) void loadPersonalizedProductPrice(productCode);
        });
      },
      { rootMargin: PRODUCT_PRICE_ROOT_MARGIN },
    );
    personalizedPriceElements.forEach((element) => observer.observe(element));

    return () => {
      isDisposed = true;
      observer.disconnect();
    };
  }, [articleId]);

  return null;
};

export default ArticleProductEnhancer;
